/**
 * Audit Tracker Configuration Provider
 *
 * Provides full context for audit events including:
 * - User identity and permissions
 * - Brand/Corporate context
 * - Location context (when applicable)
 * - Application source (admin vs core)
 *
 * Usage:
 * ```typescript
 * // In App root (after auth is initialized)
 * <AuditTrackerConfigProvider source={AuditSource.CORE_WEB}>
 *   <App />
 * </AuditTrackerConfigProvider>
 *
 * // In any component
 * const config = useAuditTrackerConfig()
 * const { trackEvent } = useAuditTracker(config)
 * ```
 */

import React, { createContext, useContext, useMemo, useState } from 'react'
import { AuditSource, AuditPlatform, AuditDeviceType } from '@hapana/shared/schemas/audit'
import { getDeviceType, getBrowserInfo, getOSInfo } from '../utils/device-info'

// =============================================================================
// Device Info Types & Utilities
// =============================================================================

interface DeviceInfo {
  deviceType: AuditDeviceType
  browserName: string
  browserVersion: string
  osName: string
  osVersion: string
  screenWidth: number
  screenHeight: number
  language: string
  timezone: string
}

/**
 * Collect device and browser information
 */
function collectDeviceInfo(): DeviceInfo {
  const browser = getBrowserInfo()
  const os = getOSInfo()

  return {
    deviceType: getDeviceType(),
    browserName: browser.name,
    browserVersion: browser.version,
    osName: os.name,
    osVersion: os.version,
    screenWidth: typeof screen !== 'undefined' ? screen.width : 0,
    screenHeight: typeof screen !== 'undefined' ? screen.height : 0,
    language: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
    timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || 'unknown',
  }
}

/**
 * Extract domain from email address
 * e.g., "user@hapana.com" -> "hapana.com"
 */
function getEmailDomain(email: string | undefined): string | undefined {
  if (!email) {
    return undefined
  }
  const parts = email.split('@')
  return parts.length === 2 ? parts[1] : undefined
}

// =============================================================================
// Types
// =============================================================================

export interface AuditTrackerContext {
  /** Application Source - identifies which app is tracking */
  source: AuditSource

  /** Platform/OS - identifies the device platform (Web, iOS, Android) */
  platform?: AuditPlatform

  /** User Context - the person performing actions */
  userId: string
  userEmail: string
  userFirstName?: string
  userLastName?: string

  /** User email domain (non-PII) e.g., "hapana.com" */
  emailDomain?: string

  /** User Permissions (for context in analytics) */
  userRoles: string[]
  userPermissions: string[]

  /** Impersonation Context - for admin Login-As functionality */
  isImpersonating: boolean
  impersonatedBy?: string // Original admin userId if impersonating

  /** Corporate/Brand Context */
  corporateId: string
  corporateSlug?: string
  corporateName?: string

  /** Location Context (when in location scope) */
  siteId?: string
  siteName?: string
  siteSlug?: string

  /** Session Context */
  sessionId: string

  /** Device Context (non-PII) */
  deviceType?: AuditDeviceType
  browserName?: string
  browserVersion?: string
  osName?: string
  osVersion?: string
  screenWidth?: number
  screenHeight?: number
  language?: string
  timezone?: string
}

export interface AuditTrackerConfigProviderProps {
  children: React.ReactNode

  /** Application source (CORE_WEB, ADMIN_WEB, MOBILE, etc.) */
  source: AuditSource

  /** Platform/OS (WEB, IOS, ANDROID) - optional, auto-detected for web */
  platform?: AuditPlatform

  /** Optional hooks to provide user data (for flexibility across apps) */
  useUserData?: () => UserDataHookResult
  useCorporateData?: () => CorporateDataHookResult
  useSiteData?: () => SiteDataHookResult
  useImpersonationData?: () => ImpersonationDataHookResult
}

// Hook result types for flexibility
export interface UserDataHookResult {
  id?: string
  email?: string
  firstName?: string
  lastName?: string
  roles?: string[]
  permissions?: string[]
}

export interface CorporateDataHookResult {
  id?: string
  slug?: string
  name?: string
}

export interface SiteDataHookResult {
  id?: string
  name?: string
  slug?: string
}

export interface ImpersonationDataHookResult {
  isImpersonating: boolean
  impersonatorUserId?: string
  impersonatorEmail?: string
}

// =============================================================================
// Session ID Management
// =============================================================================

const SESSION_ID_KEY = 'hapana_audit_session_id'

/**
 * Get or create a session ID for grouping audit events
 * Session IDs persist for the browser session (cleared on close)
 */
function getOrCreateSessionId(): string {
  // Check sessionStorage first (persists for tab lifetime)
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY)

  if (!sessionId) {
    // Generate a new session ID
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
    sessionStorage.setItem(SESSION_ID_KEY, sessionId)
  }

  return sessionId
}

// =============================================================================
// Context
// =============================================================================

const AuditTrackerConfigContext = createContext<AuditTrackerContext | null>(null)

// =============================================================================
// Default Hook Implementations
// =============================================================================

/**
 * Default user data hook - returns empty data
 * Override by passing useUserData prop
 */
function defaultUseUserData(): UserDataHookResult {
  return {}
}

/**
 * Default corporate data hook - returns empty data
 * Override by passing useCorporateData prop
 */
function defaultUseCorporateData(): CorporateDataHookResult {
  return {}
}

/**
 * Default site data hook - returns empty data
 * Override by passing useSiteData prop
 */
function defaultUseSiteData(): SiteDataHookResult {
  return {}
}

/**
 * Default impersonation data hook - returns not impersonating
 * Override by passing useImpersonationData prop
 */
function defaultUseImpersonationData(): ImpersonationDataHookResult {
  return { isImpersonating: false }
}

// =============================================================================
// Provider Component
// =============================================================================

export function AuditTrackerConfigProvider({
  children,
  source,
  platform,
  useUserData = defaultUseUserData,
  useCorporateData = defaultUseCorporateData,
  useSiteData = defaultUseSiteData,
  useImpersonationData = defaultUseImpersonationData,
}: AuditTrackerConfigProviderProps) {
  // Get session ID once on mount
  const [sessionId] = useState(() => getOrCreateSessionId())

  // Collect device info once on mount (doesn't change during session)
  const [deviceInfo] = useState<DeviceInfo | null>(() => {
    if (typeof window === 'undefined') {
      return null
    }
    return collectDeviceInfo()
  })

  // Get data from provided hooks
  const userData = useUserData()
  const corporateData = useCorporateData()
  const siteData = useSiteData()
  const impersonationData = useImpersonationData()

  // Determine platform - default to WEB for web sources
  const resolvedPlatform = useMemo(() => {
    if (platform) {
      return platform
    }
    // Auto-detect WEB for web-based sources
    if ([AuditSource.CORE_WEB, AuditSource.ADMIN_WEB, AuditSource.EMBED_WEB].includes(source)) {
      return AuditPlatform.WEB
    }
    return undefined
  }, [platform, source])

  // Build the context value
  const config: AuditTrackerContext = useMemo(
    () => ({
      source,
      platform: resolvedPlatform,

      // User context
      userId: userData.id ?? '',
      userEmail: userData.email ?? '',
      userFirstName: userData.firstName,
      userLastName: userData.lastName,
      emailDomain: getEmailDomain(userData.email),
      userRoles: userData.roles ?? [],
      userPermissions: userData.permissions ?? [],

      // Impersonation context
      isImpersonating: impersonationData.isImpersonating,
      impersonatedBy: impersonationData.impersonatorUserId,

      // Corporate context
      corporateId: corporateData.id ?? '',
      corporateSlug: corporateData.slug,
      corporateName: corporateData.name,

      // Site context
      siteId: siteData.id,
      siteName: siteData.name,
      siteSlug: siteData.slug,

      // Session
      sessionId,

      // Device context (collected once on mount)
      deviceType: deviceInfo?.deviceType,
      browserName: deviceInfo?.browserName,
      browserVersion: deviceInfo?.browserVersion,
      osName: deviceInfo?.osName,
      osVersion: deviceInfo?.osVersion,
      screenWidth: deviceInfo?.screenWidth,
      screenHeight: deviceInfo?.screenHeight,
      language: deviceInfo?.language,
      timezone: deviceInfo?.timezone,
    }),
    [
      source,
      resolvedPlatform,
      sessionId,
      deviceInfo,
      userData.id,
      userData.email,
      userData.firstName,
      userData.lastName,
      userData.roles,
      userData.permissions,
      impersonationData.isImpersonating,
      impersonationData.impersonatorUserId,
      corporateData.id,
      corporateData.slug,
      corporateData.name,
      siteData.id,
      siteData.name,
      siteData.slug,
    ]
  )

  return (
    <AuditTrackerConfigContext.Provider value={config}>
      {children}
    </AuditTrackerConfigContext.Provider>
  )
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Get the audit tracker configuration from context
 *
 * @throws Error if used outside of AuditTrackerConfigProvider
 */
export function useAuditTrackerConfig(): AuditTrackerContext {
  const context = useContext(AuditTrackerConfigContext)

  if (!context) {
    throw new Error('useAuditTrackerConfig must be used within AuditTrackerConfigProvider')
  }

  return context
}

/**
 * Get the audit tracker configuration, returning null if not available
 * Use this for optional audit tracking that shouldn't fail
 */
export function useAuditTrackerConfigOptional(): AuditTrackerContext | null {
  return useContext(AuditTrackerConfigContext)
}

// =============================================================================
// Utility Hooks for Common Patterns
// =============================================================================

/**
 * Check if we're currently in an impersonation session
 */
export function useIsImpersonating(): boolean {
  const config = useAuditTrackerConfigOptional()
  return config?.isImpersonating ?? false
}

/**
 * Get the current session ID for grouping related events
 */
export function useAuditSessionId(): string | null {
  const config = useAuditTrackerConfigOptional()
  return config?.sessionId ?? null
}

export default AuditTrackerConfigProvider
